import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSProperties } from 'styled-components';

import { iiafe } from '../../../utils/async';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';

interface Props {
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  title: string;
  confirmationText?: string;
  cancelText?: string;
  maxWidth?: CSSProperties['maxWidth'];
  negative?: boolean;
  /** Disables the confirm button, e.g. when the content is not valid. */
  confirmDisabled?: boolean;
  pos?: { x: number; y: number };
  /** When set, the dialog is portaled into this element instead of document.body. */
  portalTarget?: HTMLElement | null;
  children: ReactNode;
}
const ConfirmationDialog = ({ maxWidth = 350, ...props }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const { handleError, buildErrorList } = useErrorHandling();
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      closeButtonRef={closeButtonRef}
      confirmButtonRef={confirmButtonRef}
      maxWidth={maxWidth}
      portalTarget={props.portalTarget}
      position={props.pos ? { left: props.pos.x, top: props.pos.y } : undefined}
      onClose={props.onCancel}
    >
      {{
        header: props.title,
        content: (
          <>
            {props.children}
            {buildErrorList()}
          </>
        ),
        footer: {
          right: (
            <>
              <Button
                color={props.negative ? 'error' : 'primary'}
                disabled={loading || props.confirmDisabled}
                loading={loading}
                ref={confirmButtonRef}
                variant="contained"
                onClick={() => {
                  iiafe(async () => {
                    try {
                      setLoading(true);
                      await props.onConfirm();
                    } catch (err) {
                      handleError(err);
                    } finally {
                      setLoading(false);
                    }
                  });
                }}
              >
                {props.confirmationText || t('confirm', { ns: 'common' })}
              </Button>

              <Button
                disabled={loading}
                ref={closeButtonRef}
                variant="text"
                onClick={props.onCancel}
              >
                {props.cancelText || t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { ConfirmationDialog };
